import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/lib/cn";

/**
 * Sheet primitive
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.7
 *
 * Two visual variants:
 *   - `attached` (default): full-height, butts against the right edge,
 *     square left edge. Used for the legacy Replit-style drawer.
 *   - `floating`: top-3 bottom-3 right-3 with `rounded-xl shadow-2xl`.
 *     Used by `<SlideDrawer>` by default — leaves the underlying page
 *     visible around the drawer.
 *
 * Size scale (driven by `sm:max-w-*`):
 *   sm, md (default), lg, xl, 2xl — matches `<SlideDrawer>` exactly.
 *
 * Behavioural notes (see §2.7 + the design-doc focus-trap exception):
 *   - The Radix `Root` is `modal={false}` so focus doesn't get trapped when a
 *     nested Combobox/Popover opens. Callers must pass `modal={false}` when
 *     wrapping in `<Sheet>` directly OR use `<SlideDrawer>` which sets it.
 *   - `SheetContent` pre-suppresses `onInteractOutside`,
 *     `onPointerDownOutside`, `onFocusOutside` so nested portals don't
 *     accidentally dismiss the drawer. The custom overlay forwards its click
 *     to the `onOverlayClick` prop if you want overlay-to-close UX.
 *   - Auto-focus on open is suppressed to prevent surprise scroll jumps.
 */
const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> & {
    /** Custom click handler used by SlideDrawer for overlay-to-close UX. */
    onOverlayClick?: () => void;
  }
>(({ className, onOverlayClick, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    onClick={onOverlayClick}
    className={cn(
      "fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px]",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetSizeVariants = cva("", {
  variants: {
    size: {
      sm: "sm:max-w-sm",
      md: "sm:max-w-md",
      lg: "sm:max-w-lg",
      xl: "sm:max-w-xl",
      "2xl": "sm:max-w-2xl",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const sheetVariants = cva(
  [
    "fixed z-50 flex flex-col gap-4 bg-background text-foreground",
    "shadow-lg transition ease-in-out",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "data-[state=closed]:duration-200 data-[state=open]:duration-300",
  ].join(" "),
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top p-6",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom p-6",
        left: "inset-y-0 left-0 h-full w-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
        right:
          "inset-y-0 right-0 h-full w-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
      },
      variant: {
        // Full-height, square-edge drawer
        attached: "",
        // Floating panel — inset from the viewport edges with rounded corners
        floating:
          "top-3 bottom-3 right-3 left-auto h-auto rounded-xl shadow-2xl border-border/50",
      },
    },
    compoundVariants: [
      // Floating variant only makes sense on left/right; we use right in app.
      {
        variant: "floating",
        side: "left",
        class: "left-3 right-auto",
      },
    ],
    defaultVariants: {
      side: "right",
      variant: "attached",
    },
  }
);

export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants>,
    VariantProps<typeof sheetSizeVariants> {
  /**
   * Suppress the default close (X) button in the top-right.
   * SlideDrawer/FormDrawer render their own header so they pass `hideClose`.
   */
  hideClose?: boolean;
  /** Forwarded to the SheetOverlay so callers can wire overlay-to-close. */
  onOverlayClick?: () => void;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = "right",
      variant = "attached",
      size = "md",
      className,
      children,
      hideClose,
      onOverlayClick,
      onInteractOutside,
      onPointerDownOutside,
      onFocusOutside,
      onOpenAutoFocus,
      ...props
    },
    ref
  ) => (
    <SheetPortal>
      <SheetOverlay onOverlayClick={onOverlayClick} />
      <SheetPrimitive.Content
        ref={ref}
        // Suppress the default outside-click & focus behaviour so nested
        // Combobox/Popover portals don't fight the drawer. Callers can still
        // pass these handlers and they'll be invoked AFTER the suppression.
        onInteractOutside={(e) => {
          e.preventDefault();
          onInteractOutside?.(e);
        }}
        onPointerDownOutside={(e) => {
          e.preventDefault();
          onPointerDownOutside?.(e);
        }}
        onFocusOutside={(e) => {
          e.preventDefault();
          onFocusOutside?.(e);
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          onOpenAutoFocus?.(e);
        }}
        className={cn(
          sheetVariants({ side, variant }),
          sheetSizeVariants({ size }),
          className
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-left",
      className
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
